import { useContext, useEffect, useMemo, useState } from "react"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/common/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/common/dropdown-menu"
import useTranslator from "@pma/hooks/use-translator"
import { queryClient } from "@pma/utils/constants"
import { DictType } from "@pma/types"
import { toast } from "@pma/hooks/use-toast"
import { Loader, Plus } from "lucide-react"
import { Button } from "@pma/components/common/button"
import { GroupedTasksContext } from "./context/GroupedTasksProvider"
import { IGroupInput, IGroupMoveTaskToGroupInput } from "./api-fns"
import { Input } from "@pma/components/common/input"
import { Separator } from "@pma/components/common/separator"
import { zodResolver } from "@hookform/resolvers/zod"
import { SubmitHandler, useForm } from "react-hook-form"
import { z } from "zod"
import { useParams } from "react-router-dom"

interface Props {
  IsModalOpen: boolean
  setIsModalOpen: any
  task?: DictType
  tasks?: DictType[]
  group_id: number | null | undefined
}
interface IFormInputs {
  group_name: string
}

const schema = z.object({
  group_name: z.string().trim().min(1, "This field is required."),
})

function MoveTask({ IsModalOpen, setIsModalOpen, task, tasks, group_id }: Props) {
  const { translate } = useTranslator()
  const { projectId } = useParams()
  const [userValue, setUserValue] = useState<{
    with_users: boolean
    target_group_id: null | number
  }>({ with_users: false, target_group_id: null })

  const {
    groupedTasks,
    moveTaskToGroupMutation,
    groupedTasksLoading: groupedIsFetching,
    setSelectedTasks,
    createGroupMutation,
    groupedTasksRefetch,
  } = useContext(GroupedTasksContext)

  const {
    register,
    handleSubmit,
    formState: { isValid, isSubmitting },
    reset,
  } = useForm<IFormInputs>({
    resolver: zodResolver(schema),
  })

  const [loading, setLoading] = useState(false)
  const [searchText, setSearchText] = useState<string>("")
  const [groupList, setGroupList] = useState<any[]>([])
  const [renderButtonText, setRenderBtnText] = useState("")
  const [completedCount, setCompletedCount] = useState(0)
  const [totalTasks, setTotalTasks] = useState(0)

  useEffect(() => {
    if (totalTasks > 0 && completedCount === totalTasks) {
      setLoading(false)
      setIsModalOpen(false)
      setSelectedTasks([])
      queryClient.setQueryData(["selectedTasks"], [])
      queryClient.invalidateQueries({ queryKey: ["get-tasks"] })

      if (userValue.target_group_id) {
        localStorage.setItem("target_group_id", userValue.target_group_id.toString())
      }

      setCompletedCount(0)
      setTotalTasks(0)

      toast({
        title: translate("Success"),
        description: translate("All tasks moved successfully"),
        variant: "success",
      })
    }
  }, [completedCount, totalTasks])

  const filteredGroupTask = useMemo(() => groupedTasks, [groupedTasks])

  const isGroupsAvailable =
    filteredGroupTask?.filter((val: any) => val.id !== group_id).length > 0 ? true : false

  const handleMoveTaskIntoSelectedGroup = async () => {
    if (!userValue.target_group_id) {
      toast({
        title: translate("Error"),
        description: translate("Please select project"),
        variant: "error",
      })
      return
    }

    setLoading(true)

    const tasksToMove = task ? [task] : tasks || []

    try {
      setTotalTasks(tasksToMove.length)
      for (const tsk of tasksToMove) {
        const payload: IGroupMoveTaskToGroupInput = {
          ...userValue,
          target_group_id: userValue?.target_group_id == -1 ? null : userValue?.target_group_id,
          task_id: tsk.taskId || tsk.id, // fallback to id if taskId doesn't exist
          inform_users: true,
        }

        await new Promise<void>((resolve, reject) => {
          moveTaskToGroupMutation?.mutate(payload, {
            onSuccess: () => {
              setCompletedCount((pre) => pre + 1)
              resolve()
            },
            onError: (error: any) => {
              const errorMessage = error?.response?.data?.message
              setCompletedCount((pre) => pre + 1)
              reject(errorMessage || "Failed to move task")
            },
          })
        })
      }
    } catch (err) {
      setLoading(false)
      toast({
        title: translate("Error"),
        description: err || translate("Failed to move tasks"),
        variant: "error",
      })
    }
  }

  const handleChange = (name: string, value: number) => {
    setUserValue((prev) => {
      return { ...prev, [name]: value }
    })
  }

  const handleCancel = () => {
    setIsModalOpen(false)
  }

  useEffect(() => {
    if (filteredGroupTask)
      setGroupList(filteredGroupTask?.filter((val: any) => val.id !== group_id))
  }, [filteredGroupTask])

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const filtered = filteredGroupTask
        .filter((group: DictType) => group.name.toLowerCase().includes(searchText.toLowerCase()))
        ?.filter((val: any) => val.id !== group_id)
      setGroupList(filtered)
    }, 300) // Debounce delay in ms

    return () => clearTimeout(delayDebounce) // Cleanup timeout
  }, [searchText])

  const onSubmit: SubmitHandler<IFormInputs> = (data) => {
    if (!projectId) return
    const payload: IGroupInput = {
      ...data,
      project_id: parseInt(projectId),
    }
    createGroupMutation?.mutate(payload)
  }

  useEffect(() => {
    if (!createGroupMutation?.isPending && createGroupMutation?.isSuccess) {
      groupedTasksRefetch()
      reset()
    }
  }, [createGroupMutation?.isPending])

  return (
    <Dialog open={IsModalOpen} onOpenChange={setIsModalOpen}>
      <DialogContent className='w-max min-w-[15vw]'>
        <DialogHeader>
          <DialogTitle className='py-2'>{translate("Move task to other group")}</DialogTitle>
          <DialogDescription className='flex h-[80px] w-full flex-row justify-evenly gap-5 rounded-md border p-5'>
            <div>
              <DropdownMenu>
                <DropdownMenuTrigger
                  className='outline-none focus:outline-none focus-visible:outline-none'
                  asChild
                >
                  <Button
                    variant={"outline"}
                    size={"sm"}
                    className='w-max outline-none focus:outline-none focus-visible:outline-none'
                    disabled={!isGroupsAvailable}
                  >
                    {isGroupsAvailable
                      ? userValue.target_group_id
                        ? renderButtonText
                        : translate("Select group")
                      : translate("No groups available")}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align='start'
                  className='max-h-[250px] min-w-fit !overflow-scroll'
                >
                  <Input
                    value={searchText}
                    placeholder={translate("Search Group")}
                    onChange={(e) => setSearchText(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                  <Separator className='my-2' />
                  {groupList.length > 0 ? (
                    groupList.map((val: any) => {
                      return (
                        <DropdownMenuItem
                          onClick={() => {
                            setRenderBtnText(val.name)
                            handleChange("target_group_id", val.id)
                            setSearchText("")
                          }}
                        >
                          {val.name}
                        </DropdownMenuItem>
                      )
                    })
                  ) : (
                    <p className='text-center'>{translate("No Group")}</p>
                  )}

                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className='mb-2 flex w-full flex-wrap gap-2 sm:flex-nowrap xl:mb-0'
                  >
                    <Input
                      placeholder={translate("New Group")}
                      className='h-8 w-full'
                      {...register("group_name", { required: true })}
                    />
                    <Button
                      className='ml-1'
                      size={"icon"}
                      loading={createGroupMutation?.isPending || isSubmitting}
                      disabled={createGroupMutation?.isPending || !isValid}
                      type='submit'
                    >
                      {createGroupMutation?.isPending || isSubmitting ? "" : <Plus size={16} />}
                    </Button>
                  </form>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className='flex w-full gap-4'>
          <DialogClose className='w-full'>
            <Button variant={"outline"} className='w-full' size={"default"} onClick={handleCancel}>
              {translate("Cancel")}
            </Button>
          </DialogClose>
          <div className='flex w-full items-center justify-center'>
            <Button
              size={"default"}
              className='w-full'
              variant={"default"}
              disabled={groupedIsFetching || loading}
              onClick={handleMoveTaskIntoSelectedGroup}
            >
              {groupedIsFetching || loading ? <Loader /> : translate("Move")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default MoveTask
